//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { LayoutContext } from '../../common/contexts';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getCurrencyText, getYearsOfExperienceText, nl2br, numberWithCommas } from '../../common/textHelper';
import { getStateLabelByCountryName } from '../../common/locationHelper';
import { CONFIRM_DIALOG_MESSAGE_CHANGE_JS_PROFILE_TO_DRAFT, LABEL_CURRENCY, LABEL_PAY_FREQUENCY } from '../../common/constants';
import {
    PLACEHOLDER_FIELD_FIRST_NAME,
    PLACEHOLDER_FIELD_MIDDLE_NAME,
    PLACEHOLDER_FIELD_LAST_NAME,
    PLACEHOLDER_FIELD_COUNTRY,
    PLACEHOLDER_FIELD_STATE,
    PLACEHOLDER_FIELD_CITY,
    PLACEHOLDER_FIELD_EMPLOYMENT_TYPE,
    PLACEHOLDER_FIELD_RATE_EXACT,
    PLACEHOLDER_FIELD_RATE_MIN,
    PLACEHOLDER_FIELD_RATE_MAX,
    PLACEHOLDER_FIELD_OCCUPATION,
    PLACEHOLDER_FIELD_SKILLSET,
    PLACEHOLDER_FIELD_EDUCATION,
    PLACEHOLDER_FIELD_OTHER_EDUCATION,
    PLACEHOLDER_FIELD_ABOUT_ME,
    PLACEHOLDER_FIELD_CURRENCY,
    PLACEHOLDER_FIELD_PAY_FREQUENCY,
    PLACEHOLDER_FIELD_GENDER,
    PLACEHOLDER_FIELD_RACE,
    PLACEHOLDER_FIELD_YEARS_OF_EXPERIENCE,
} from '../../common/placeholderHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { changeProfileDraft } from '../../common/services/myProfileServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdMultiSelectLovValueBlock from '../../common/elemental/AfitdMultiSelectLovValueBlock';
// import AfitdLink from '../../common/elemental/AfitdLink';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import PlaceholderText from './PlaceholderText';
import AboutMeDialog from './AboutMeDialog';
import FieldLabel from './FieldLabel';
import FieldLabelWithTrackStatInfo from './FieldLabelWithTrackStatInfo';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import { format } from 'date-fns';
import _get from 'lodash/get';
// import _set from 'lodash/set';
import _some from 'lodash/some';
import _isNil from 'lodash/isNil';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import _isArray from 'lodash/isArray';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        root: {
            backgroundColor: '#FFFFFF',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
        },
        // .aboutCnt.mght h4
        titleContainer: { padding: '0 0 19px 0' },
        // .aboutMain
        editButtonContainer: {
            // textAlign: 'right',
        },
        // .aboutMain
        editButton: { fontSize: '16px', color: '#FC821F', textTransform: 'none' },
        // .fault
        bodyContainer: { padding: '0 0 54px 0' },
        // .fault ul li label{width: 100%;font-family: 'Roboto', sans-serif;font-size: 12px;line-height: 19px;color: #111E14;font-weight: 500;display: block;padding: 0 0 8px 0;}
        // .fault ul li
        fieldContainer: { margin: '0 0 27px 0' },
        fieldRow: { display: 'flex', justifyContent: 'space-between', [theme.breakpoints.down('sm')]: { flexDirection: 'column' } },
        fieldColumnStart: {
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            marginRight: '5px',
            width: '50%',
            [theme.breakpoints.down('sm')]: { width: '100%', marginRight: '0px', marginBottom: 27 },
        },
        fieldColumnEnd: {
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            marginLeft: '5px',
            width: '50%',
            [theme.breakpoints.down('sm')]: { width: '100%', marginLeft: '0px' },
        },
        // .fault ul li.place input{border: 1px dashed #D5D7DD;width: 100%;height: 65px;display: block;font-family: 'Roboto', sans-serif;font-size: 14px;line-height: 17px;color: #111E14;font-weight: 500;padding: 0 10px;border-radius: 5px;}
        // .fault ul li input
        emptyField: {
            border: '1px dashed #D5D7DD',
            // width: '100%',
            // height: '16px',
            display: 'block',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            lineHeight: '16px',
            color: 'rgba(17, 30, 20, 0.4)',
            fontWeight: 400,
            padding: '9px 10px',
            borderRadius: '5px',
        },
        field: {
            border: '1px solid #D5D7DD',
            // width: '100%',
            // height: '16px',
            display: 'block',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            lineHeight: '16px',
            color: 'rgba(17, 30, 20, 0.4)',
            fontWeight: 400,
            padding: '9px 10px',
            borderRadius: '5px',
        },
        // .fault ul li textarea
        emptyFieldMultiline: {
            border: '1px dashed #D5D7DD',
            // width: '100%',
            minHeight: '54px',
            display: 'block',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            lineHeight: '17px',
            color: 'rgba(17, 30, 20, 0.4)',
            fontWeight: 'normal',
            padding: '12px 20px',
            borderRadius: '5px',
            overflow: 'hidden',
            resize: 'none',
        },
        fieldMultiline: {
            border: '1px solid #D5D7DD',
            // width: '100%',
            minHeight: '54px',
            display: 'block',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            lineHeight: '17px',
            color: 'rgba(17, 30, 20, 0.4)',
            fontWeight: 'normal',
            padding: '12px 20px',
            borderRadius: '5px',
            overflow: 'hidden',
            resize: 'none',
        },
        populatedLovField: { display: 'flex' },
        populatedLovValueBlock: { marginRight: '8px', padding: '9px 10px', borderRadius: '4px', background: '#1D8A8B' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, props, makeProfilePublicDialog, setMakePofilePublicDialog, onConfrimChangeProfileType } = params;
    const { data, editDialogOpen, setEditDialogOpen } = props;
    // const editLinkLabel = isFormEmpty(data) ? 'Add' : 'Edit';
    const stateLabel = getStateLabelByCountryName(_get(data, 'country'));
    // const isProfilePublic = _get(data, 'isPublic');

    const currency = _get(data, 'currency');
    const currencyText = currency ? getCurrencyText(currency, _get(data, 'currencySymbol')) : null;

    const defns = {
        titleText: {
            variant: 'h6',
            clsLightBold: true,
            value: 'About Me',
        },
        // editLink: {
        //     label: editLinkLabel,
        //     onClick: () => {
        //         if (isProfilePublic) setMakePofilePublicDialog(true);
        //         else setEditDialogOpen(true);
        //     },
        // },
        editDialog: {
            open: editDialogOpen,
            data,
            onClose: handleCloseEditDialog.bind(null, setEditDialogOpen),
        },
        firstName: {
            data: _get(data, 'firstName'),
        },
        middleName: {
            data: _get(data, 'middleName'),
        },
        lastName: {
            data: _get(data, 'lastName'),
        },
        gender: {
            data: _get(data, 'gender'),
        },
        race: {
            data: _get(data, 'race'),
        },
        city: {
            data: _get(data, 'city'),
        },
        state: {
            stateLabel,
            data: _get(data, 'state'),
        },
        country: {
            data: _get(data, 'country'),
        },
        yearsOfExperience: {
            data: getYearsOfExperienceText(_get(data, 'yearsOfExperience')),
        },
        employmentType: {
            data: _get(data, 'employmentType'),
        },
        salaryRate: {
            isExactRate: _get(data, 'isExactRate'),
            rateMin: _get(data, 'rateMin'),
            rateMax: _get(data, 'rateMax'),
        },
        currency: {
            data: currencyText, //getCurrencyText(_get(data, 'currency'), _get(data, 'currencySymbol')),
        },
        payFrequency: {
            data: _get(data, 'payFrequency'),
        },
        occupation: {
            data: _get(data, 'occupation'),
        },
        skillset: {
            data: _get(data, 'skillset'),
        },
        education: {
            data: _get(data, 'education'),
        },
        otherEducation: {
            data: _get(data, 'otherEducation'),
        },
        aboutMeField: {
            data: _get(data, 'aboutMe'),
        },
        willingRelocate: {
            data: _get(data, 'willingRelocate'),
        },
        makeProfilePublicDialog: {
            maxWidth: 'sm',
            title: 'Edit Your Profile?',
            onClose: () => setMakePofilePublicDialog(false),
            onConfirm: onConfrimChangeProfileType,
            open: makeProfilePublicDialog,
            message: CONFIRM_DIALOG_MESSAGE_CHANGE_JS_PROFILE_TO_DRAFT,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

// const handleClickEditLink = (setEditDialogOpen, evt) => {
//     setEditDialogOpen(true);
// };

const handleCloseEditDialog = (setEditDialogOpen, evt) => {
    setEditDialogOpen(false);
};

const isFormEmpty = (fields = []) => {
    return !_some(fields, item => {
        if (_isArray(item)) {
            return !_isEmpty(item);
        } else {
            return !_isNil(item) && item !== '';
        }
    });
};

const getFieldLabel = props => {
    const { label = '', fieldLabelComponent, isDisplayColon = true } = props;

    return (
        fieldLabelComponent || (
            <FieldLabel>
                {label}
                {isDisplayColon ? ':' : ''}
            </FieldLabel>
        )
    );
};

const EmptyField = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { placeholder = '', isMultiline = false, containerDefns: containerDefnsProp } = props;

    let defns = {};
    setClassNameInDefns(defns, classes);
    const fieldDefns = isMultiline ? defns.emptyFieldMultiline : defns.emptyField;
    const containerDefns = containerDefnsProp || defns.fieldContainer;

    return (
        <div {...containerDefns}>
            {getFieldLabel(props)}
            <div {...fieldDefns}>
                <PlaceholderText value={placeholder} />
            </div>
        </div>
    );
};

const PopulatedField = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { isMultiline = false, value = '', containerDefns: containerDefnsProp } = props;

    let defns = {
        valueText: {
            variant: 'body2',
            style: { lineHeight: '16px' },
        },
    };
    setClassNameInDefns(defns, classes);
    const containerDefns = containerDefnsProp || defns.fieldContainer;

    if (isMultiline) {
        return (
            <div {...containerDefns}>
                {getFieldLabel(props)}
                <div {...defns.fieldMultiline}>
                    <AfitdTypography {...defns.valueText}>{nl2br(value)}</AfitdTypography>
                </div>
            </div>
        );
    } else {
        return (
            <div {...containerDefns}>
                {getFieldLabel(props)}
                <div {...defns.field}>
                    <AfitdTypography {...defns.valueText}>{value}</AfitdTypography>
                </div>
            </div>
        );
    }
};

const PopulatedMultiSelectLovField = props => {
    const { data = [], isGrey } = props;
    const classes = useStyles(/*{param1, param2}*/)();

    let defns = {};
    setClassNameInDefns(defns, classes);

    const lovValueBlockDefnList = _map(data, value => {
        return { isGrey, value };
    });

    return (
        <div {...defns.fieldContainer}>
            {getFieldLabel(props)}
            <div {...defns.populatedLovField}>
                {_map(lovValueBlockDefnList, lovValueBlockDefn => (
                    <AfitdMultiSelectLovValueBlock {...lovValueBlockDefn} />
                ))}
            </div>
        </div>
    );
};

const FirstName = props => {
    // const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    if (isFormEmpty([data])) {
        return <EmptyField label="First Name" placeholder={PLACEHOLDER_FIELD_FIRST_NAME} />;
    } else {
        return <PopulatedField label="First Name" value={data} />;
    }
};

const MiddleName = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    let defns = {};
    setClassNameInDefns(defns, classes);

    if (isFormEmpty([data])) {
        return <EmptyField label="Middle Name" placeholder={PLACEHOLDER_FIELD_MIDDLE_NAME} containerDefns={defns.fieldColumnStart} />;
    } else {
        return <PopulatedField label="Middle Name" value={data} containerDefns={defns.fieldColumnStart} />;
    }
};

const LastName = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    let defns = {};
    setClassNameInDefns(defns, classes);

    if (isFormEmpty([data])) {
        return <EmptyField label="Last Name" placeholder={PLACEHOLDER_FIELD_LAST_NAME} containerDefns={defns.fieldColumnEnd} />;
    } else {
        return <PopulatedField label="Last Name" value={data} containerDefns={defns.fieldColumnEnd} />;
    }
};

const Gender = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    let defns = {};
    setClassNameInDefns(defns, classes);

    const fieldLabelComponent = <FieldLabelWithTrackStatInfo classes={classes}>Gender</FieldLabelWithTrackStatInfo>;

    if (isFormEmpty([data])) {
        return <EmptyField fieldLabelComponent={fieldLabelComponent} placeholder={PLACEHOLDER_FIELD_GENDER} containerDefns={defns.fieldColumnStart} />;
    } else {
        return <PopulatedField fieldLabelComponent={fieldLabelComponent} value={data} containerDefns={defns.fieldColumnStart} />;
    }
};

const Race = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    let defns = {};
    setClassNameInDefns(defns, classes);

    const fieldLabelComponent = <FieldLabelWithTrackStatInfo classes={classes}>Race</FieldLabelWithTrackStatInfo>;

    if (isFormEmpty([data])) {
        return <EmptyField fieldLabelComponent={fieldLabelComponent} placeholder={PLACEHOLDER_FIELD_RACE} containerDefns={defns.fieldColumnEnd} />;
    } else {
        return <PopulatedField fieldLabelComponent={fieldLabelComponent} value={data} containerDefns={defns.fieldColumnEnd} />;
    }
};

const Country = props => {
    // const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    if (isFormEmpty([data])) {
        return <EmptyField label="Country" placeholder={PLACEHOLDER_FIELD_COUNTRY} />;
    } else {
        return <PopulatedField label="Country" value={data} />;
    }
};
const YearsOfExperience = props => {
    // const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    if (isFormEmpty([data])) {
        return <EmptyField label="Years of Experience" placeholder={PLACEHOLDER_FIELD_YEARS_OF_EXPERIENCE} />;
    } else {
        return <PopulatedField label="Years of Experience" value={data} />;
    }
};

const TwoFieldRow = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { children } = props;

    let defns = {};
    setClassNameInDefns(defns, classes);

    return (
        <div {...defns.fieldContainer}>
            <div {...defns.fieldRow}>{children}</div>
        </div>
    );
};

const City = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    let defns = {};
    setClassNameInDefns(defns, classes);

    if (isFormEmpty([data])) {
        return <EmptyField label="City" placeholder={PLACEHOLDER_FIELD_CITY} containerDefns={defns.fieldColumnStart} />;
    } else {
        return <PopulatedField label="City" value={data} containerDefns={defns.fieldColumnStart} />;
    }
};

const State = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { stateLabel = 'State', data } = props;

    let defns = {};
    setClassNameInDefns(defns, classes);

    if (isFormEmpty([data])) {
        return <EmptyField label={stateLabel} placeholder={PLACEHOLDER_FIELD_STATE} containerDefns={defns.fieldColumnEnd} />;
    } else {
        return <PopulatedField label={stateLabel} value={data} containerDefns={defns.fieldColumnEnd} />;
    }
};

const EmploymentType = props => {
    // const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    if (isFormEmpty([data])) {
        return <EmptyField label="Desired Employment Type" placeholder={PLACEHOLDER_FIELD_EMPLOYMENT_TYPE} />;
    } else {
        return <PopulatedMultiSelectLovField label="Desired Employment Type" data={data} />;
    }
};

const WillingToRelocate = props => {
    const { data: willingRelocate } = props;
    const classes = useStyles(/*{param1, param2}*/)();

    let defns = {};
    setClassNameInDefns(defns, classes);

    if (!willingRelocate) {
        return null;
    } else {
        return (
            <div {...defns.fieldContainer}>
                {getFieldLabel({ label: 'Willing to Relocate', isDisplayColon: false })}
                <div {...defns.populatedLovField}>
                    <AfitdMultiSelectLovValueBlock value={'YES'} isExtraPadding />
                </div>
            </div>
        );
    }
};

const SalaryRate = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { isExactRate, rateMin, rateMax } = props;

    let defns = {};
    setClassNameInDefns(defns, classes);

    if (isExactRate) {
        if (isFormEmpty([rateMin])) {
            return <EmptyField label="Desired Salary/Rate" placeholder={PLACEHOLDER_FIELD_RATE_EXACT} />;
        } else {
            return <PopulatedField label="Desired Salary/Rate" value={`$${numberWithCommas(rateMin)}`} />;
        }
    } else {
        return (
            <TwoFieldRow>
                {isFormEmpty([rateMin]) && <EmptyField label="Minimum Desired Salary/Rate" placeholder={PLACEHOLDER_FIELD_RATE_MIN} containerDefns={defns.fieldColumnStart} />}
                {!isFormEmpty([rateMin]) && <PopulatedField label="Minimum Desired Salary/Rate" value={`$${numberWithCommas(rateMin)}`} containerDefns={defns.fieldColumnStart} />}
                {isFormEmpty([rateMax]) && <EmptyField label="Maximum Desired Salary/Rate" placeholder={PLACEHOLDER_FIELD_RATE_MAX} containerDefns={defns.fieldColumnEnd} />}
                {!isFormEmpty([rateMax]) && <PopulatedField label="Maximum Desired Salary/Rate" value={`$${numberWithCommas(rateMax)}`} containerDefns={defns.fieldColumnEnd} />}
            </TwoFieldRow>
        );
    }
};

const Currency = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    let defns = {};
    setClassNameInDefns(defns, classes);

    if (isFormEmpty([data])) {
        return <EmptyField label={LABEL_CURRENCY} placeholder={PLACEHOLDER_FIELD_CURRENCY} containerDefns={defns.fieldColumnStart} />;
    } else {
        return <PopulatedField label={LABEL_CURRENCY} value={data} containerDefns={defns.fieldColumnStart} />;
    }
};

const PayFrequency = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    let defns = {};
    setClassNameInDefns(defns, classes);

    if (isFormEmpty([data])) {
        return <EmptyField label={LABEL_PAY_FREQUENCY} placeholder={PLACEHOLDER_FIELD_PAY_FREQUENCY} containerDefns={defns.fieldColumnEnd} />;
    } else {
        return <PopulatedField label={LABEL_PAY_FREQUENCY} value={data} containerDefns={defns.fieldColumnEnd} />;
    }
};

const Occupation = props => {
    // const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    if (isFormEmpty([data])) {
        return <EmptyField label="Desired Occupation" placeholder={PLACEHOLDER_FIELD_OCCUPATION} />;
    } else {
        return <PopulatedMultiSelectLovField label="Desired Occupation" data={data} isGrey={true} />;
    }
};

const Skillset = props => {
    // const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    if (isFormEmpty([data])) {
        return <EmptyField label="Skillset" placeholder={PLACEHOLDER_FIELD_SKILLSET} />;
    } else {
        return <PopulatedMultiSelectLovField label="Skillset" data={data} />;
    }
};

const Education = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    if (isFormEmpty([data])) {
        return <EmptyField label="Education" placeholder={PLACEHOLDER_FIELD_EDUCATION} />;
    } else {
        let defns = {};
        setClassNameInDefns(defns, classes);
        return <PopulatedField label="Education" value={data} />;
    }
};

const OtherEducation = props => {
    // const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    if (isFormEmpty([data])) {
        return <EmptyField label="Other Education" placeholder={PLACEHOLDER_FIELD_OTHER_EDUCATION} />;
    } else {
        return <PopulatedMultiSelectLovField label="Other Education" data={data} isGrey={true} />;
    }
};

const AboutMeField = props => {
    // const classes = useStyles(/*{param1, param2}*/)();
    const { data } = props;

    if (isFormEmpty([data])) {
        return <EmptyField label="About Me" placeholder={PLACEHOLDER_FIELD_ABOUT_ME} isMultiline />;
    } else {
        return <PopulatedField label="About Me" value={data} isMultiline />;
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const AboutMe = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const { userId } = useContext(AppContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = useContext(LayoutContext);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [editDialogOpen, setEditDialogOpen] = useState();
    const [makeProfilePublicDialog, setMakePofilePublicDialog] = useState(false);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { editDialogOpen } = props;

    //---------------------------------------------------------------------
    // Handlers
    //---------------------------------------------------------------------
    const onConfrimChangeProfileType = async () => {
        await changeProfileDraft({ userId, setSnackbarMessage, setSnackbarErrorMessage });
    };

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, makeProfilePublicDialog, setMakePofilePublicDialog, onConfrimChangeProfileType });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.header}>
                <div {...defns.titleContainer}>
                    <AfitdTypography {...defns.titleText} />
                </div>
                {/* <div {...defns.editButtonContainer}>
                    <AfitdLink {...defns.editLink} />
                </div> */}
            </div>
            <div {...defns.bodyContainer}>
                {/* <Grid container justify="space-between" spacing={3}>
                    <Grid item container {...defns.fieldRow}>
                        <FirstName {...defns.firstName} />
                    </Grid>
                    <Grid item container {...defns.fieldRow}>
                        <Grid item {...defns.halfItem}>
                            <MiddleName {...defns.middleName} />
                        </Grid>
                        <Grid item {...defns.halfItem}>
                            <LastName {...defns.lastName} />
                        </Grid>
                    </Grid>
                </Grid> */}
                <div>
                    <FirstName {...defns.firstName} />
                    <TwoFieldRow>
                        <MiddleName {...defns.middleName} />
                        <LastName {...defns.lastName} />
                    </TwoFieldRow>
                    <TwoFieldRow>
                        <Gender {...defns.gender} />
                        <Race {...defns.race} />
                    </TwoFieldRow>
                    <TwoFieldRow>
                        <City {...defns.city} />
                        <State {...defns.state} />
                    </TwoFieldRow>
                    <Country {...defns.country} />
                    <YearsOfExperience {...defns.yearsOfExperience} />
                    <WillingToRelocate {...defns.willingRelocate} />
                    <EmploymentType {...defns.employmentType} />
                    <SalaryRate {...defns.salaryRate} />
                    <TwoFieldRow>
                        <Currency {...defns.currency} />
                        <PayFrequency {...defns.payFrequency} />
                    </TwoFieldRow>
                    <Occupation {...defns.occupation} />
                    <Skillset {...defns.skillset} />
                    <Education {...defns.education} />
                    <OtherEducation {...defns.otherEducation} />
                    <AboutMeField {...defns.aboutMeField} />
                </div>
            </div>
            <AfitdConfirmDialog {...defns.makeProfilePublicDialog} />
            {editDialogOpen && <AboutMeDialog {...defns.editDialog} fullScreen={true} maxWidth="lg" />}
        </div>
    );
};

export default AboutMe;
